import { useCallback, useMemo } from 'react';
import { capitalize } from 'lodash';
import FilterDropdown from 'components/elements/Dropdown/FilterDropdown';
import { useCurrentlySelectedCities } from 'state/currentLocation';
import { useDashboardDistricts, useDashboardZoneExclusions } from 'state/dashboard';
import { useZones } from 'state/projectTypes';

const ZonesFilter = () => {
  const [districtsGroup, setDistrictsGroup] = useDashboardDistricts();
  const [zoneExclusions, setZoneExclusions] = useDashboardZoneExclusions();
  const { currentlySelectedCitiesIds } = useCurrentlySelectedCities();
  const { getZonesForCity, getAllCityLuds } = useZones(
    currentlySelectedCitiesIds.join(','),
  );

  const zonesSorted = useMemo(() => {
    const sorted = getZonesForCity
      // The REGEX is a hack fix to remove the prefix for "1-4 arts and entertainments" zone in San Antonio
      .sort((a, b) =>
        a.name.replace(/^1-4 /, '').localeCompare(b.name.replace(/^1-4 /, '')),
      )
      .filter(({ name }) => name !== '')
      .map(({ name, children }) => ({
        label: capitalize(name.replace(/^1-4 /, '')),
        value: name,
        children: children?.map(({ name: childName, description }) => ({
          label: capitalize(childName),
          value: childName,
          description,
        })),
      }));

    return sorted;
  }, [getZonesForCity]);
  const zonesSubmitted = useCallback(
    (values: string[], isParent: boolean) => {
      const currentSet = new Set(districtsGroup);

      if (isParent) {
        getZonesForCity.forEach((zone) => {
          const childrenIds = zone.children?.map((child) => child.id) || [];

          if (values.includes(zone.name)) {
            // Check for partial selection
            const alreadySelectedChildren = childrenIds.filter((id) =>
              currentSet.has(id),
            );
            if (
              alreadySelectedChildren.length > 0 &&
              alreadySelectedChildren.length < childrenIds.length
            ) {
              // If parent is partially selected, only remove unselected children
              childrenIds.forEach((id) => {
                if (!alreadySelectedChildren.includes(id)) {
                  currentSet.delete(id);
                }
              });
            } else {
              // Else, add all children of the selected parent
              childrenIds.forEach((id) => currentSet.add(id));
            }
          } else {
            // If this parent is deselected, check if its children are not individually selected
            // If they are not, then remove them. Otherwise, leave them.
            childrenIds.forEach((id) => {
              if (
                !values.some(() =>
                  getZonesForCity.some(
                    (z) =>
                      z.name !== zone.name &&
                      z.children?.some((child) => child.id === id),
                  ),
                )
              ) {
                currentSet.delete(id);
              }
            });
          }
        });
      } else {
        // For child zones
        getZonesForCity.forEach((zone) => {
          zone.children?.forEach((childZone) => {
            if (values.includes(childZone.name)) {
              currentSet.add(childZone.id);
            } else {
              currentSet.delete(childZone.id);
            }
          });
        });
      }

      setDistrictsGroup(Array.from(currentSet));
    },
    [getZonesForCity, districtsGroup],
  );

  const exclusionsSubmitted = useCallback(
    (values: string[], isParent: boolean) => {
      const currentSet = new Set(zoneExclusions);

      if (isParent) {
        getZonesForCity.forEach((zone) => {
          const childrenIds = zone.children?.map((child) => child.id) || [];

          if (values.includes(zone.name)) {
            // Check for partial selection
            const alreadySelectedChildren = childrenIds.filter((id) =>
              currentSet.has(id),
            );
            if (
              alreadySelectedChildren.length > 0 &&
              alreadySelectedChildren.length < childrenIds.length
            ) {
              // If parent is partially selected, only remove unselected children
              childrenIds.forEach((id) => {
                if (!alreadySelectedChildren.includes(id)) {
                  currentSet.delete(id);
                }
              });
            } else {
              // Else, add all children of the selected parent
              childrenIds.forEach((id) => currentSet.add(id));
            }
          } else {
            // If this parent is deselected, check if its children are not individually selected
            // If they are not, then remove them. Otherwise, leave them.
            childrenIds.forEach((id) => {
              if (
                !values.some(() =>
                  getZonesForCity.some(
                    (z) =>
                      z.name !== zone.name &&
                      z.children?.some((child) => child.id === id),
                  ),
                )
              ) {
                currentSet.delete(id);
              }
            });
          }
        });
      } else {
        // For child zones
        getZonesForCity.forEach((zone) => {
          zone.children?.forEach((childZone) => {
            if (values.includes(childZone.name)) {
              currentSet.add(childZone.id);
            } else {
              currentSet.delete(childZone.id);
            }
          });
        });
      }

      setZoneExclusions(Array.from(currentSet));
    },
    [getZonesForCity, zoneExclusions, setZoneExclusions],
  );

  return (
    <FilterDropdown
      options={zonesSorted}
      onSubmit={(values: string[], isParent: boolean) => zonesSubmitted(values, isParent)}
      checkedValues={districtsGroup.map(
        (id) => getAllCityLuds().find((lud) => lud.id === id)?.district || '',
      )}
      childValues={districtsGroup.map(
        (id) => getAllCityLuds().find((lud) => lud.id === id)?.code || '',
      )}
      setCheckedValues={setDistrictsGroup}
      setChildValues={setDistrictsGroup}
      onClearAll={() => setDistrictsGroup([])}
      isExclusion
      setExclusionValues={setZoneExclusions}
      exclusionValues={zoneExclusions.map(
        (id: number) => getAllCityLuds().find((lud) => lud.id === id)?.district || '',
      )}
      exclusionChildValues={zoneExclusions.map(
        (id) => getAllCityLuds().find((lud) => lud.id === id)?.code || '',
      )}
      onExclusionSubmit={(values: string[], isParent: boolean) =>
        exclusionsSubmitted(values, isParent)
      }
      setChildExclusionValues={setZoneExclusions}
      onExclusionClearAll={() => setZoneExclusions([])}
      description="Property zoning classifications that regulate land use and development"
    >
      Zones
    </FilterDropdown>
  );
};

export default ZonesFilter;
